import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../components/Button.tsx";
import ConfirmDialog from "../components/ConfirmDialog.tsx";
import TopNav, { AppNavMode } from "../components/TopNav.tsx";
import { getScores } from "../services/scores.ts";
import { cancelSubscription } from "../services/subscription.ts";

interface Score {
  num_questions: number;
  num_correct: number;
  subject: string;
  topic: string;
}

const Dashboard: React.FC = () => {
  const [scores, setScores] = useState<Score[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelMessage, setCancelMessage] = useState("");

  useEffect(() => {
    const fetchScores = async () => {
      setLoading(true);
      setError("");
      try {
        const user = {
          email: localStorage.getItem("email") || "",
          password: localStorage.getItem("password") || ""
        };
        const response = await getScores();
        setScores(response.data);
      } catch (err: any) {
        setError("Failed to fetch scores");
      } finally {
        setLoading(false);
      }
    };
    fetchScores();
  }, []);

  // Button to navigate to /questions
  const navigate = useNavigate();

  const handleModeNav = (mode: AppNavMode) => {
    if (mode === "practice-exam") {
      navigate("/practice-exam");
      return;
    }
    navigate(`/questions?mode=${mode}`);
  };

  const handleCancelSubscription = async () => {
    setCancelling(true);
    setCancelMessage("");
    try {
      const response = await cancelSubscription();
      const endsAt = new Date(response.data.subscription_ends_at).toLocaleDateString();
      setCancelMessage(`Your subscription will end on ${endsAt}.`);
    } catch (err: any) {
      setCancelMessage("Failed to cancel subscription. Please try again.");
    } finally {
      setCancelling(false);
      setShowCancelDialog(false);
    }
  };

  return (
    <div className="min-h-screen">
      <TopNav activeMode="dashboard" onSelect={handleModeNav} />
      <div className="max-w-3xl mx-auto px-4 md:px-8 py-6">
        <h2 className="text-4xl font-bold font-display text-ink text-center mb-8">Your Score Dashboard</h2>
        {cancelMessage && (
          <div className="text-lg text-center mb-4 font-body text-primary">{cancelMessage}</div>
        )}
        <div className="flex justify-center gap-3 mb-6">
          <Button onClick={() => navigate("/questions")}>Go to Quiz</Button>
          <Button onClick={() => navigate("/practice-exam")}>Practice Exam</Button>
          <Button variant="secondary" onClick={() => setShowCancelDialog(true)}>
            Cancel Subscription
          </Button>
        </div>
        {loading ? (
          <div className="text-xl font-body text-gray-500 text-center">Loading...</div>
        ) : error ? (
          <div className="text-xl font-body text-incorrect text-center">{error}</div>
        ) : scores.length === 0 ? (
          <div className="bg-white rounded-lg border border-border p-12 text-center text-lg font-body text-gray-600">
            No scores yet. Take a quiz to see your results here!
          </div>
        ) : (
          <div className="bg-white rounded-lg border border-border overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="bg-lightbg text-ink text-lg font-semibold font-body">
                  <th className="py-3 px-6 text-left">Subject</th>
                  <th className="py-3 px-6 text-left">Topic</th>
                  <th className="py-3 px-6 text-left">Questions</th>
                  <th className="py-3 px-6 text-left">Correct</th>
                </tr>
              </thead>
              <tbody>
                {scores.map((score, idx) => (
                  <tr key={idx} className="text-lg font-body text-ink border-b border-border last:border-b-0">
                    <td className="py-3 px-6">{score.subject}</td>
                    <td className="py-3 px-6">{score.topic}</td>
                    <td className="py-3 px-6">{score.num_questions}</td>
                    <td className="py-3 px-6">{score.num_correct}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ConfirmDialog
          open={showCancelDialog}
          title="Cancel Subscription"
          message="Are you sure you want to cancel your subscription? You will lose access at the end of your current billing period."
          confirmLabel="Yes, cancel it"
          cancelLabel="Keep subscription"
          loading={cancelling}
          onConfirm={handleCancelSubscription}
          onCancel={() => setShowCancelDialog(false)}
        />
      </div>
    </div>
  );
};

export default Dashboard;
